import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { asyncScheduler, delay, distinctUntilChanged, skip, takeUntil } from 'rxjs';

import { ScrollIntoViewBlock, WindowRefService } from '@hdms/shared/util-core';

import { ButtonSliderAbstractComponent, Direction } from '../shared/button-slider/button-slider-component.abstract';

import { Tab } from './tab';
import { TabAbstractComponent } from './tab-abstract.component';
import { TabService } from './tab.service';

const TAB_ALIGN_DELAY = 100;

@Component({
  selector: 'shrd-uic-tab-slider',
  templateUrl: './tab-slider.component.html',
  styleUrls: ['./tab-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabSliderComponent extends ButtonSliderAbstractComponent implements OnInit, OnDestroy {
  override readonly direction = Direction;

  private tabElements: TabAbstractComponent[];

  // workaround to retrieve all Tabs from tab-group as Angular has a bug
  // for getting QueryLists from nested ng-content
  @Input()
  set tabs(tabs: TabAbstractComponent[]) {
    this.tabElements = tabs;
    this.sliderElements = tabs.map(t => t.element.nativeElement);
  }

  private readonly win = inject(WindowRefService).nativeWindow;
  private readonly tabService = inject(TabService);

  override ngOnInit(): void {
    super.ngOnInit();
    this.restoreScrollPosition();
    this.subscribeToTabChange();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.activeTab && this.tabService.savePosition(this.activeTab.id, this.scrollLeft);
  }

  protected override breakpointChange(): void {
    this.scrollToActiveTab();
  }

  private restoreScrollPosition(): void {
    const savedPos = this.tabService.getSavedPosition(this.activeTab?.id);
    if (!isNaN(savedPos)) {
      this.slideTo(savedPos, false);
      // re-align tab with small delay, in case restoring position leads to intersected tab position.
      asyncScheduler.schedule(() => this.alignActiveItem(this.getActiveTabElement()), TAB_ALIGN_DELAY);
    } else {
      this.scrollToActiveTab();
    }
  }

  private scrollToActiveTab(): void {
    const tab = this.getActiveTabElement();
    this.win.requestAnimationFrame(() => {
      this.updateDimensions();
      if (tab && this.scrollerContentWidth > this.scrollerWidth) {
        tab.scrollIntoView({
          block: ScrollIntoViewBlock.NEAREST,
          inline: ScrollIntoViewBlock.CENTER
        });
        this.syncShiftWithScrollLeft();
      } else {
        this.resetShift();
      }
    });
  }

  /**
   * Re-align tab on tab change.
   * Skip first active change emit, but only track subsequent changes,
   * as those are results of user interaction.
   * Use a delay to prevent alignment, in case onDestroy
   * happens short after tab change (e.g. by navigation event).
   * @private
   */
  private subscribeToTabChange(): void {
    this.tabService.change
      .pipe(skip(1), distinctUntilChanged(), delay(TAB_ALIGN_DELAY), takeUntil(this.unsubscribe$))
      .subscribe(({ id }) => this.alignActiveItem(this.getActiveTabElement(id)));
  }

  private get activeTab(): Tab {
    return this.tabService.activeTab;
  }

  private getActiveTabElement(id?: string): HTMLElement {
    return this.tabElements?.find(t => (id ? t.isActive({ id }) : t.active))?.element.nativeElement;
  }
}
